import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { RequestOptions } from './types';
import type { paths } from '../schema';
import { curry1 } from './utils';

type UpdateFileResponse =
  paths['/api/files/{id}']['patch']['responses']['200']['content']['application/json'];

type UploadFileResponse = {
  common_key: string | null;
  content_type: string;
  create: string;
  id: string;
  is_common: boolean;
  is_public: boolean;
  key: string;
  name: string;
  size_bytes: number;
  size_formatted: string;
  thumbnail_url: string;
  url: string;
};

type Source =
  | 'activity_export'
  | 'activity_field_value'
  | 'automation_value'
  | 'employee_export'
  | 'employee_import'
  | 'field_value'
  | 'form_submission_export'
  | 'form_field_value'
  | 'public_image'
  | 'record_export'
  | 'record_import'
  | 'survey_submission_export'
  | 'smart_connector_import';

type UploadFile = File & { $id: string };

type UploadFileOptions =
  | {
      source?: Exclude<Source, 'form_field_value'>;
      publicFile?: boolean;
      handleProgress?(x: { id: string; progress: number }): void;
      usePublicAPI?: boolean;
    }
  | {
      source: 'form_field_value';
      formId: string;
      publicFile?: boolean;
      handleProgress?(x: { id: string; progress: number }): void;
      usePublicAPI?: boolean;
    };

type UploadS3Options = {
  fields: Record<string, string>;
  url: string;
  s3object_id: string;
  filename: string;
  mime: string;
  file: UploadFile;
  handleProgress?(opts: { id: string; progress: number }): void;
};

type SuccessOptions = {
  key: string;
  uuid: string;
  etag: string;
  filename: string;
  businessId: string;
  publicFile?: boolean;
  source?: Source;
  usePublicAPI?: boolean;
};

export const MAX_FILE_SIZE_BYTES = 5e8; // 500mb

const urlEncodeForm = (obj: object) => {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, value]) => {
    params.set(key, value);
  });
  return params.toString();
};

const getSignature = async (
  instance: AxiosInstance,
  filename: string,
  mime: string,
  source?: Source,
  businessId?: string,
  formId?: string,
  usePublicAPI?: boolean
) => {
  const {
    data: { fields, url, s3object_id },
  } = await instance.get(
    usePublicAPI ? '/public/s3/presigned-post' : '/s3/presigned-post',
    {
      params: { source, form_id: formId, filename, contenttype: mime },
      headers: {
        ...(businessId ? { 'x-business-id': businessId } : {}),
      },
    }
  );

  return { fields, url, s3object_id };
};

const uploadToS3 = async (
  instance: AxiosInstance,
  { fields, url, filename, mime, file, handleProgress }: UploadS3Options
) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }
  formData.append('file', file);

  const {
    headers: { etag },
  } = await instance.post('/', formData, {
    // Setting this tells the AxiosService to skip the v2 API auth, which isn't relevant to S3
    baseURL: url,
    timeout: undefined,
    withCredentials: false,
    headers: {
      'content-type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (handleProgress) {
        if (progressEvent.progress) {
          handleProgress?.({
            id: file.$id,
            progress: Math.round(progressEvent.progress * 100),
          });
        }
      }
    },
  });

  return { key: fields.key, filename, mime, etag };
};

const onSuccess = async (
  instance: AxiosInstance,
  {
    key,
    uuid,
    etag,
    filename,
    businessId,
    publicFile = false,
    source = 'field_value',
    usePublicAPI = false,
  }: SuccessOptions
): Promise<UploadFileResponse> => {
  const payload = urlEncodeForm({
    key,
    uuid,
    name: filename,
    etag,
    // Since this endpoint expects "form encoding" (supposedly),
    // we have to use python style "True" of "False"
    is_public: publicFile ? 'True' : 'False',
    ...(businessId && { business_id: businessId }),
  });
  const config = {
    params: {
      source: source, // I believe when this endpoint is hit from the frontend, "form" is the only appropriate value
    },
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
    },
  };
  const res = await instance.post<UploadFileResponse>(
    usePublicAPI ? '/public/s3/success' : '/s3/success',
    payload,
    config
  );
  return res.data;
};

export const deleteFile = async (
  instance: AxiosInstance,
  id: string,
  key: string,
  options?: RequestOptions
): Promise<void> => {
  const config: AxiosRequestConfig = {
    ...options,
    params: { key },
  };

  await instance.delete(`/files/${id}`, config);
};

export const updateFile = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/files/{id}']['patch']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<UpdateFileResponse> => {
  const res = await instance.patch<UpdateFileResponse>(
    `/files/${id}`,
    data,
    options
  );

  return res.data;
};

const customMediaTypes: Record<string, string> = {
  kzn: 'application/kzn+zip',
};

export const uploadFile = async (
  instance: AxiosInstance,
  file: UploadFile,
  id: string,
  businessId: string,
  options: UploadFileOptions = {}
): Promise<UploadFileResponse> => {
  const {
    source = 'field_value',
    publicFile = false,
    handleProgress,
  } = options;
  const formId =
    options.source === 'form_field_value' ? options.formId : undefined;
  const fileExtension = file.name.split('.').pop() || 'txt';
  const mediaType =
    file.type || customMediaTypes[fileExtension] || `text/${fileExtension}`; // browser in windows cannot detect mime correct
  const { fields, url, s3object_id } = await getSignature(
    instance,
    file.name,
    mediaType,
    source,
    businessId,
    formId,
    options.usePublicAPI
  );
  const { etag } = await uploadToS3(instance, {
    fields,
    url,
    s3object_id,
    filename: file.name,
    mime: mediaType,
    file,
    handleProgress,
  });

  return onSuccess(instance, {
    key: fields.key,
    uuid: id,
    etag,
    filename: file.name,
    businessId,
    publicFile,
    source,
    usePublicAPI: options.usePublicAPI,
  });
};

export class FileService {
  public deleteFile;
  public updateFile;
  public uploadFile;

  constructor(private instance: AxiosInstance) {
    this.deleteFile = curry1(deleteFile)(this.instance);
    this.updateFile = curry1(updateFile)(this.instance);
    this.uploadFile = curry1(uploadFile)(this.instance);
  }
}
